import { getBudgetTotals } from "./CaseModelUtility.ts";
import { damageName } from "./damages.ts";
import { Case, Claim, getAwardedValue, Issue, Quantum } from "./CaseModel.ts";
import { Budget } from "./CostModel.ts";
import { leavesMissingWinChance } from "./winChance.ts";

/**
 * What a case must contain before its analytics mean anything.
 *
 * The engine never fails on missing input — it substitutes a default and
 * produces a confident answer: an unset win chance becomes a certainty, absent
 * claimed amounts make every outcome worth nothing, and absent costs collapse
 * the net result onto the expected value. So the check has to happen before the
 * calculation rather than being inferred from its output.
 *
 * Used in three places, which is the point: the server gates the calculation on
 * it, the analytics area renders a checklist from it, and the case drawer badges
 * the configuration sections from it. One definition, so they cannot disagree.
 */

export type PrerequisiteId =
  | "parties"
  | "claims"
  | "amounts"
  | "winChances"
  | "costs"
  | "feeStructure";

export interface Prerequisite {
  id: PrerequisiteId;
  met: boolean;
  /**
   * Unmet, and something it depends on is unmet too — so it cannot be acted on
   * yet. Sending someone to claims before any party exists just bounces them
   * back to parties.
   */
  blocked: boolean;
  /** Case-derived context for the row — party names, the claim title, a count. */
  detail?: string;
  /** For `winChances`: how many leaf issues still carry no entered chance. */
  missing?: number;
}

/**
 * What each prerequisite needs before it can be entered at all. Not a chain:
 * costs are per party and can be entered with no claim in sight, while amounts
 * and win chances hang off a claim.
 */
const DEPENDENCIES: { [id in PrerequisiteId]: PrerequisiteId[] } = {
  parties: [],
  claims: ["parties"],
  amounts: ["claims"],
  winChances: ["claims"],
  costs: ["parties"],
  feeStructure: [],
};

/** Where each prerequisite is entered, relative to the case root. */
export const PREREQUISITE_ROUTES: { [id in PrerequisiteId]: string } = {
  parties: "configuration/parties",
  claims: "configuration/claims",
  amounts: "configuration/claims",
  winChances: "configuration/claims",
  costs: "configuration/costs",
  feeStructure: "configuration/costStructure",
};

const MAX_DETAIL_ITEMS = 3;

// a client and an opponent
const MIN_PARTIES = 2;

const summarizeNames = (items: string[]): string =>
  items.length > MAX_DETAIL_ITEMS
    ? `${items.slice(0, MAX_DETAIL_ITEMS).join(", ")}, +${
        items.length - MAX_DETAIL_ITEMS
      }`
    : items.join(", ");

/**
 * True when this claim is worth something if it succeeds. A claim with no
 * amount contributes nothing to any figure, so it is worth pointing out per
 * claim — even though the case-level prerequisite is satisfied by any one of
 * them carrying an amount.
 */
export function claimHasClaimedAmount(claim: Claim): boolean {
  return quantumsOfClaim(claim).some((q) => getAwardedValue(q) > 0);
}

/** Every quantum on a claim, including those hanging off its issues. */
function quantumsOfClaim(claim: Claim): Quantum[] {
  const collected: Quantum[] = [];
  const walk = (issue: Issue) => {
    if (issue.quantums) collected.push(...issue.quantums);
    issue.subIssues?.forEach(walk);
  };
  walk(claim);
  claim.issues?.forEach(walk);
  return collected;
}

/** True when a budget holds any cost, totalled exactly as calculateResults does. */
export function budgetHasCosts(budget: Budget): boolean {
  const total = getBudgetTotals(budget);
  return (
    total.getIncurredTotal().cmp(0) !== 0 ||
    total.getEstimatedTotal().cmp(0) !== 0
  );
}

function partiesWithCosts(c: Case): string[] {
  return (c.budgets ?? []).filter(budgetHasCosts).map(({ partyId }) => partyId);
}

/**
 * Parties with no costs entered. Not a prerequisite — costs for any one party
 * are enough for the calculation to run — but each such party's net result
 * leaves out what it spends, so they are flagged per party rather than gated.
 */
export function partiesWithoutCosts(c: Case): string[] {
  const funded = new Set(partiesWithCosts(c));
  return (c.parties ?? []).map(({ id }) => id).filter((id) => !funded.has(id));
}

/**
 * The five prerequisites that gate the calculation, in dependency order.
 * `feeStructure` is deliberately absent — it gates only the Revenue view, see
 * {@link feeStructurePrerequisite}.
 */
export function casePrerequisites(c: Case): Prerequisite[] {
  const parties = c.parties ?? [];
  const claims = c.claims ?? [];

  // a claim only counts once it has someone to run against
  const claimsWithDefendants = claims.filter(
    (claim) => (claim.defendants?.length ?? 0) > 0,
  );

  const amounts = claims
    .flatMap(quantumsOfClaim)
    .filter((quantum) => getAwardedValue(quantum) > 0);

  // vacuously true with no claims, so gate on claims existing too — otherwise
  // an empty case would show win chances as satisfied
  const missingWinChances = leavesMissingWinChance(claims);

  const funded = partiesWithCosts(c);

  const found: Omit<Prerequisite, "blocked">[] = [
    {
      id: "parties",
      met: parties.length >= MIN_PARTIES,
      detail:
        parties.length > 0
          ? summarizeNames(parties.map((p) => p.name))
          : undefined,
    },
    {
      id: "claims",
      met: claimsWithDefendants.length > 0,
      detail:
        claimsWithDefendants.length > 0
          ? summarizeNames(claimsWithDefendants.map((claim) => claim.title))
          : undefined,
    },
    {
      id: "amounts",
      met: amounts.length > 0,
      detail:
        amounts.length > 0
          ? summarizeNames([
              ...new Set(amounts.map((q) => damageName(c.damages, q.damageId))),
            ])
          : undefined,
    },
    {
      id: "winChances",
      met: claims.length > 0 && missingWinChances.length === 0,
      missing: missingWinChances.length,
      detail:
        missingWinChances.length > 0
          ? summarizeNames(missingWinChances.map((issue) => issue.title))
          : undefined,
    },
    {
      id: "costs",
      met: funded.length > 0,
      detail:
        funded.length > 0
          ? summarizeNames(
              funded.map(
                (partyId) =>
                  parties.find((p) => p.id === partyId)?.name ?? partyId,
              ),
            )
          : undefined,
    },
  ];

  const isMet = (id: PrerequisiteId) =>
    found.find((p) => p.id === id)?.met ?? true;

  // a dependency that is itself blocked blocks this one too, so the chain
  // resolves without needing the list to be topologically ordered
  const blockedBy = (id: PrerequisiteId): boolean =>
    DEPENDENCIES[id].some((on) => !isMet(on) || blockedBy(on));

  return found.map((p) => ({ ...p, blocked: !p.met && blockedBy(p.id) }));
}

/** Revenue analytics additionally need the client fee structure. */
export function feeStructurePrerequisite(c: Case): Prerequisite {
  return {
    id: "feeStructure",
    met: c.costStructure?.clientFeeStructure !== undefined,
    blocked: false,
  };
}

/** True when the calculation would produce meaningful numbers. */
export function analyticsCanRun(c: Case): boolean {
  return casePrerequisites(c).every(({ met }) => met);
}
